import GoogleTagManager1 from './google-tag-manager-1.js'
import Segment1 from './segment-1.js'
import Console from './console.js'
import AnalyticsHandler from './handler.js'
import SmartlookBusiness from './smartlook-business.js'
import WebViewHandler1 from './web-view-handler-1.js'
import { HANDLER_NAMES } from '@/lib/analytics/consts.ts'
import { WEBVIEW_TYPES } from '@/lib/consts.ts'
import { convertArrayToString } from './helpers.js'

export default class AnalyticsManager {
  constructor ({
    logger,
    webView,
    country,
  }) {
    this.logger = logger
    this.webView = webView
    this.country = country
    this.__addHandlers()
  }

  __addHandlers () {
    const isProduction = window.location.hostname === 'booksy.com'

    this.handlers = []

    if (isProduction) {
      this.__addProductionHandlers()
    } else {
      this.__addDevelopmentHandlers()
    }
  }

  __addProductionHandlers () {
    this.addHandler({
      name: HANDLER_NAMES.GTM_1,
      code: 'GTM-KZZS4BX',
      handler: GoogleTagManager1,
      eventConverters: [ convertArrayToString ],
    })

    this.addHandler({
      name: HANDLER_NAMES.ANALYTICS,
      code: 'k5CqA1NY5o12nl4gMREbxM4FzJA6sAFG',
      handler: Segment1,
    })

    if ([WEBVIEW_TYPES.ANDROID, WEBVIEW_TYPES.IOS].includes(this.webView)) {
      this.addHandler({
        name: HANDLER_NAMES.WEB_VIEW_HANDLER_1,
        handler: WebViewHandler1,
        eventConverters: [ convertArrayToString ],
      })
    }

    // recording for onboarding web process run in ioswebview...
    if ([WEBVIEW_TYPES.IOS].includes(this.webView)) {
      // ...for US only
      if (this.country === 'us') {
        this.addHandler({
          name: HANDLER_NAMES.SMARTLOOK_BUSINESS,
          code: '86908f46d53c5ed23fa034101a78c16d55bab508', // project: BIZ_Onboarding_iOS
          handler: SmartlookBusiness,
          appConfig: {
            hoursLimit: 5,
          },
        })
      }
    } else {
      this.addHandler({
        name: HANDLER_NAMES.SMARTLOOK_BUSINESS,
        code: '4af46ecd36269e4412983057035299084273f9dd', // project: BIZ_Overall_Web
        handler: SmartlookBusiness,
        appConfig: {
          hoursLimit: 5,
        },
      })
    }
  }

  __addDevelopmentHandlers () {
    this.addHandler({
      name: HANDLER_NAMES.DEBUG,
      handler: Console,
    })

    this.addHandler({
      name: HANDLER_NAMES.GTM_1,
      code: 'GTM-KZZS4BX',
      handler: GoogleTagManager1,
      eventConverters: [ convertArrayToString ],
    })

    this.addHandler({
      name: HANDLER_NAMES.ANALYTICS,
      code: 'W2bU2JP8nfdZMPhCp7Qjb7ea9VOSy2vy',
      handler: Segment1,
    })

    if ([WEBVIEW_TYPES.ANDROID, WEBVIEW_TYPES.IOS].includes(this.webView)) {
      this.addHandler({
        name: HANDLER_NAMES.WEB_VIEW_HANDLER_1,
        handler: WebViewHandler1,
        eventConverters: [ convertArrayToString ],
      })
    }

    // recording for onboarding web process run in ioswebview for us only
    if ([WEBVIEW_TYPES.IOS].includes(this.webView)) {
      // ...for US only
      if (this.country === 'us') {
        this.addHandler({
          name: HANDLER_NAMES.SMARTLOOK_BUSINESS,
          code: '86908f46d53c5ed23fa034101a78c16d55bab508', // project: BIZ_Onboarding_iOS
          handler: SmartlookBusiness,
          appConfig: {
            hoursLimit: 5,
          },
        })
      }
    } else {
      this.addHandler({
        name: HANDLER_NAMES.SMARTLOOK_BUSINESS,
        code: '4af46ecd36269e4412983057035299084273f9dd', // project: BIZ_Overall_Web
        handler: SmartlookBusiness,
        appConfig: {
          hoursLimit: 5,
        },
      })
    }
  }

  addHandler (config) {
    try {
      this.__validateHandler(config)
      const HandlerClass = config.handler
      const handler = new HandlerClass(config, this)
      this.handlers.push(handler)
    } catch (e) {
      this.logger.error(e)
    }
  }

  __validateHandler (config) {
    if (!(config.handler.prototype instanceof AnalyticsHandler)) {
      throw new Error(`Handler doesn't has AnalyticsHandler as a base class`)
    }

    if (!config.name) {
      throw new Error('Handler name must be defined')
    }

    if (this.findHandler(config)) {
      throw new Error(`Handler with name ${config.name} already exists`)
    }
  }

  findHandler (config) {
    if (!config) {
      return null
    }

    return this.handlers.find(handler => handler.name === config.name)
  }

  dispatch (data) {
    if (!Array.isArray(this.handlers)) {
      return
    }

    this.handlers.forEach(handler => {
      try {
        handler.dispatch(data)
      } catch (e) {
        this.logger.error(e)
      }
    })
  }

  removeHandler (handler) {
    if (this.handlers.includes(handler)) {
      const handlerIndex = this.handlers.indexOf(handler)
      this.handlers.splice(handlerIndex, 1)
    }

    this.logger.debug('remove handler', { removedHandler: handler, handlers: this.handlers })
  }
}
